/**
 * FaceClipPlayer — loads and plays pre-converted face animation clips.
 *
 * Each clip is a JSON file with per-frame ARKit blendshape weights
 * (produced by aosTools/threejs/convert_face_fbx_to_arkit.py).
 *
 * The face graph evaluator produces weights like { listening_neutral_face: 0.8 }
 * and a play request per Play Clip node. This player returns the blended
 * 52-element ARKit weight array for the current time, which HeadModel applies
 * alongside LiveLink data. Every playhead is its own — one per face Play Clip
 * node, one per manual play — as the body's are (see getBlendedWeights).
 */

import { playBodyGesture } from './GestureChannel'
import { devLog } from './devLog'
import { resolveActiveCharacter } from './characterState'
import { listAnimationAssets, listGestures } from './assetManagerApi.js'
import { buildGestureMap } from './gestureFields.js'
// Aliased: the method below is also called gestureHasFace, and an unqualified
// call inside a class body resolves to the module scope, not the method — true,
// but it reads as recursion.
import { resolveGestureEntry, gestureHasFace as mapHasFaceClip } from './gestureLookup.js'
// The gesture-duration default is NOT imported: resolveGestureTiming applies it
// internally, so nothing here needs the number. It lives in gestureTiming.js.
import { resolveGestureTiming, overrideWeightAt } from './gestureTiming.js'
import { clipRuntimeKeys } from './clipRuntimeKeys.js'
import { fetchWithRetry } from './fetchRetry.js'

// Stock face clips + gesture mappings now live in the DB+S3, served by
// the per-character animation-assets + gestures endpoints. The legacy
// public/assets/animations and public/assets/gestures.json fetches are gone.

// Defaults for gesture playback when a mapping doesn't override them.
const DEFAULT_FACE_WEIGHT         = 0.8

class FaceClipPlayer {
  constructor() {
    this._clips = {}     // { name: { fps, kind?, frames: [{ timeCode, blendshapeWeights }] } }
    // { name: playhead } — the clips that played in the last getBlendedWeights
    // call (the heavier playhead of a clip). A VIEW, rebuilt each call; the
    // playheads themselves live on what they belong to (see getBlendedWeights).
    this._active = {}
    this._overrides = {} // { name: { weight, expiresAt, fadeOutMs, playhead } } — manual plays
    this._byName = {}    // { name: playhead } — clips the graph names with no play request
    this._gestures = {}  // { gestureName: { face: clipName } }
    this._loaded = false
    // Reusable output buffer — zero-filled each frame instead of allocating a new array
    this._resultBuffer = new Float32Array(52)
    // One output buffer per value kind (a clip's `kind`, default 'arkit'); ARKit's
    // is the 52-channel buffer getBlendedWeights returns.
    this._kindBuffers = new Map([['arkit', this._resultBuffer]])
    this._kindHas = { arkit: false }
    this._call = 0                 // getBlendedWeights call counter (playhead continuity)
    this._playedNames = new Set()  // scratch: clips a play request covered this call
  }

  /**
   * Play a named gesture by server-sent tag (e.g. "smile", "leans in").
   * Looks up gestures.json, resolves to a clip, triggers a manualPlay.
   * Case-insensitive; substring-matches so multi-word server tags still
   * resolve to short keys (e.g. "leans in, grin spreads" → "lean in").
   *
   * @param {string} gestureName
   * @param {object} [opts] { body } — pass `{ body: false }` to play the face
   *   half only. Used when a kimodo-GENERATED body clip is driving the gesture:
   *   GestureChannel is a single slot, so letting the mapping's stock `body`
   *   clip play too would clobber the generated one (last play wins).
   */
  playGesture(gestureName, { body = true, durationMs: durationOverride = null, fadeOutMs: fadeOverride = null } = {}) {
    if (!gestureName) return
    const entry = resolveGestureEntry(this._gestures, gestureName)
    if (!entry) {
      devLog(`[FaceClipPlayer] No gesture mapping for "${gestureName}" (and no "*" fallback)`)
      return
    }

    // Blank overrides inherit the row's duration and leave each half's own
    // fade-out default alone — see gestureTiming.js for why those two defaults
    // must not be collapsed into one.
    const { durationMs, fadeOutMs } = resolveGestureTiming(entry, {
      durationMs: durationOverride,
      fadeOutMs:  fadeOverride,
    })
    const parts = []

    // Face clip (if the mapping has one) — loaded via FaceClipPlayer's manualPlay.
    if (entry.face) {
      this.manualPlay(entry.face, DEFAULT_FACE_WEIGHT, durationMs, fadeOutMs)
      parts.push(`face=${entry.face}`)
    }

    // Body clip (if the mapping has one) — layered via GestureChannel on top
    // of the SM-driven base animation. The body action must already exist in
    // window.__AVATAR_ACTIONS__ (i.e. referenced from manifest.json body[]).
    if (entry.body && body) {
      playBodyGesture(entry.body, {
        durationMs,
        peakWeight: entry.bodyWeight ?? undefined,
        // Omitted when blank so playBodyGesture keeps its own DEFAULT_FADE_MS.
        ...(fadeOutMs !== null ? { fadeOutMs } : {}),
      })
      parts.push(`body=${entry.body}`)
    }

    if (parts.length === 0) {
      devLog(`[FaceClipPlayer] Gesture "${gestureName}" has no face or body mapping`)
    } else {
      devLog(`[FaceClipPlayer] gesture "${gestureName}" -> ${parts.join(', ')} (${durationMs}ms)`)
    }
  }

  /**
   * Manually play a clip for `durationMs` at the given weight, ignoring the
   * face graph. Used by the PlayFaceClip debug blueprint node to test clips
   * without needing to drive the state machine.
   */
  manualPlay(name, weight = 1.0, durationMs = 5000, fadeOutMs = null) {
    if (!this._clips[name]) {
      console.warn(`[FaceClipPlayer] manualPlay: clip not loaded: ${name}`)
      return
    }
    // `fadeOutMs` null (every caller but Play Gesture's override) keeps the
    // historical hard expiry — overrideWeightAt is a no-op without it.
    // A new object, so a new playhead: playing a clip again starts it at frame 0,
    // also while the graph or an earlier manual play has it mid-way.
    this._overrides[name] = { weight, expiresAt: performance.now() + durationMs, fadeOutMs, playhead: null }
  }

  /** Load all face clips from the manifest + gestures.json. Call once on startup.
   *
   * Failures throw — never silently fall back to an empty clip set or empty
   * gesture map. The App-level error boundary renders a visible overlay so
   * the user knows the backend / asset pipeline is broken. See
   * docs/UX_PRINCIPLES.md.
   *
   * Per-clip JSON fetch failures are still collected and warned (one bad
   * clip shouldn't take down the whole avatar), but the manifest and
   * gestures.json themselves are non-negotiable.
   */
  async loadAll() {
    const charConfig = await resolveActiveCharacter()
    const slug = charConfig?.id
    if (!slug) throw new Error('FaceClipPlayer.loadAll: no character slug')

    // Pull face clips from the DB+S3 — same animation-assets endpoint
    // AvatarModel uses for body clips, just filtered to kind=face.
    const all = await listAnimationAssets(slug)
    const faceAssets = (all ?? []).filter(a => a.kind === 'face')

    const failed = []
    const clipsP = Promise.all(faceAssets.map(async (asset) => {
      const { name, file_url: url } = asset
      try {
        // Retry transient network blips (net::ERR_NETWORK_CHANGED under CI
        // load) so one dropped request doesn't silently drop a face clip.
        const res = await fetchWithRetry(url)
        if (!res.ok) { console.warn(`[FaceClipPlayer] Failed to load ${url}: ${res.status}`); failed.push(name); return }
        const data = await res.json()
        this._clips[name] = data
        // Every runtime key this asset resolves by, registered by its OWNER as
        // the clip lands. The `custom_face_<id>` alias is the same JSON under a
        // second name (clipRuntimeKeys.js), so CustomAssetManager used to
        // re-download the file to produce it. Doing it here costs nothing and
        // means the alias is never missing for a clip that is present.
        for (const key of clipRuntimeKeys(asset)) this.aliasClip(key, name)
      } catch (e) {
        console.warn(`[FaceClipPlayer] Error loading ${url}:`, e)
        failed.push(name)
      }
    }))

    // Gesture mappings come from the gestures table: each row references
    // a body and/or face animation_asset by ID; we resolve those back to
    // clip names (which is what _gestures consumers expect at runtime).
    const gesturesP = this.refreshGestures({ slug, assets: all })

    await Promise.all([clipsP, gesturesP])
    this._loaded = true
    const loaded = Object.keys(this._clips)
    console.log(`[FaceClipPlayer] ${loaded.length} clips ready: ${loaded.join(', ')}` + (failed.length ? ` | FAILED: ${failed.join(', ')}` : ''))
  }

  /**
   * Rebuild the gesture map from the gestures endpoint, REPLACING it whole.
   *
   * Split out of loadAll so a gesture edit in the Composer reaches the runtime
   * without re-downloading every face clip. Nothing used to call it at all:
   * the map was built once at avatar mount and CustomAssetManager's
   * loadGestureIntoRuntime was a documented no-op, so a saved gesture kept
   * playing its OLD clips (and a new one never appeared) until a page reload.
   *
   * Replaced whole rather than merged, because a DELETED gesture has to leave
   * the map — a merge would keep playing a tag the server no longer has.
   *
   * Throws on a failed fetch. The caller decides what a stale map means: at
   * boot it takes down the avatar load (loadAll's contract), on a save the
   * Composer surfaces it. Never a silently empty map.
   *
   * @param {object} [opts]
   * @param {string} [opts.slug]   character slug; resolved from the active
   *   character when absent.
   * @param {Array}  [opts.assets] animation assets, to resolve ids -> clip
   *   names. Re-fetched when absent (a gesture save may have added one).
   */
  async refreshGestures({ slug, assets } = {}) {
    const charSlug = slug ?? (await resolveActiveCharacter())?.id
    if (!charSlug) throw new Error('FaceClipPlayer.refreshGestures: no character slug')
    const [rows, all] = await Promise.all([
      listGestures(charSlug),
      assets ? Promise.resolve(assets) : listAnimationAssets(charSlug),
    ])
    this._gestures = buildGestureMap(rows, all)
    console.log(`[FaceClipPlayer] Gesture map: ${Object.keys(this._gestures).join(', ')}`)
    return this._gestures
  }

  /**
   * Whether the gesture `name` resolves to carries a FACE clip.
   *
   * Same resolution playGesture runs (gestureLookup.js), so the answer can
   * never disagree with what actually plays. Read by App.jsx to put a
   * `faceGesture` pin on the sentence-ready events, letting the event graph
   * decide per sentence whether the gesture needs a lead before speech.
   *
   * Reports false before the gesture map has loaded — there is genuinely no
   * face clip to play yet. The sentence-ready events fire mid-response, long
   * after the avatar mounted, so that window is not reachable in practice;
   * the devLog is there for when it somehow is.
   */
  gestureHasFace(name) {
    if (!this._loaded) devLog(`[FaceClipPlayer] gestureHasFace("${name}") before the gesture map loaded — reporting false`)
    return mapHasFaceClip(this._gestures, name)
  }

  /** True if a clip is loaded under `name`. Lets callers resolve the actual
   * runtime key (seeded clips register under their name; custom uploads also
   * under custom_face_<id>) before calling manualPlay. */
  hasClip(name) {
    return Object.prototype.hasOwnProperty.call(this._clips, name)
  }

  /** Register a face clip at runtime (e.g. from a custom upload). */
  loadCustomClip(name, clipData) {
    this._clips[name] = clipData
  }

  /**
   * Point a SECOND key at an already-loaded clip — no re-fetch, no re-parse.
   *
   * Every animation asset also resolves by a `custom_face_<assetId>` alias
   * (clipRuntimeKeys.js), which names the same JSON as the asset's own name.
   * Producing it by re-downloading the file was waste; this is the free path.
   *
   * The frame data is SHARED, not copied, unlike the body-clip alias
   * (__ALIAS_BODY_CLIP__ has to clone because three.js caches an action per clip
   * object). Face clips are read-only per-frame weight arrays with no playback
   * state on them — each playhead is kept by what plays it (getBlendedWeights) —
   * so two keys over one array is correct here and costs nothing.
   *
   * @returns {boolean} whether `alias` now resolves.
   */
  aliasClip(alias, sourceName) {
    if (!alias || alias === sourceName) return false
    if (this.hasClip(alias)) return true          // idempotent
    if (!this.hasClip(sourceName)) return false   // source not loaded (yet)
    this._clips[alias] = this._clips[sourceName]
    return true
  }

  /** Register a gesture tag → clip/body mapping at runtime. */
  registerGestureMapping(tag, mapping) {
    this._gestures[tag.toLowerCase().trim()] = mapping
  }

  /**
   * Stop a manually played clip (its override), as clearOverride does. The graph's
   * own plays of a clip are not stopped here: they follow the graph.
   */
  stop(name) {
    this.clearOverride(name)
  }

  /**
   * Release ONE manual override and stop its clip — the single-clip half of
   * cancelAllOverrides, for a preview that knows which clip it started.
   * Cancelling all of them instead would kill a gesture's face half that a
   * conversation is mid-way through playing.
   */
  clearOverride(name) {
    if (!name) return
    delete this._overrides[name]
    delete this._active[name]
  }

  /** Cancel all manual overrides immediately (called by stopSpeech). */
  cancelAllOverrides() {
    for (const k in this._overrides) delete this._overrides[k]
  }

  /**
   * Write per-clip timings ({time,duration,remaining,ratio}) for every face
   * clip that played in the last getBlendedWeights call directly into the
   * supplied target — typically `debugState.clipTimings`. When two playheads
   * share a clip the heavier is reported. Reuses existing entry objects in
   * place. Stale entries (clips no longer face-driven) are dropped here so
   * callers don't see ghost timings.
   */
  writeTimingsInto(target) {
    if (!target) return
    for (const k in target) {
      if (this._clips[k] && !this._active[k]) delete target[k]
    }
    for (const name in this._active) {
      const st = this._active[name]
      const clip = this._clips[name]
      if (!clip?.frames?.length) continue
      const duration = clip.frames[clip.frames.length - 1].timeCode
      if (duration <= 0) continue
      const time = clipTime(st, duration)
      const existing = target[name]
      if (existing) {
        existing.time      = time
        existing.duration  = duration
        existing.remaining = duration - time
        existing.ratio     = time / duration
      } else {
        target[name] = { time, duration, remaining: duration - time, ratio: time / duration }
      }
    }
  }

  /**
   * The blended weights of one value KIND from the last getBlendedWeights call,
   * or null when no clip of that kind played. A clip declares its kind
   * (`clip.kind`, default 'arkit'); each kind is summed into its own buffer,
   * sized from the clip, so a non-ARKit clip (GNM expression coefficients, say)
   * never lands in the 52 ARKit channels. Reused buffer — copy what you keep.
   */
  getBlendedKind(kind) {
    return this._kindHas[kind] ? this._kindBuffers.get(kind) : null
  }

  /**
   * Get blended 52-element ARKit weights for all active clips.
   *
   * Every playhead is its own: each face Play Clip node's play request carries
   * one (re-entering its state starts it over; its Loop and Speed apply), each
   * manual play (manualPlay: gestures' face halves, previews, prefabs) has one
   * from the moment it was started, and a clip the graph names with no Play
   * Clip node behind it (First / Last Frame Pose) plays by name. They SUM —
   * a manual play no longer shares, or takes the max with, the graph's
   * playhead on the same clip.
   *
   * @param {Object} faceWeights - { clipName: weight } from the face graph evaluator
   * @param {object[]} [plays] - the face graph's play requests (evaluateGraph().plays);
   *   a clip they name is played from them, not from faceWeights
   * @returns {Float32Array|null} Blended 52-element array, or null if nothing active
   */
  getBlendedWeights(faceWeights, plays = null) {
    if (!this._loaded) return null

    // Hot path — called every frame. No per-frame allocation: per-playhead
    // state lives on the request / override / by-name record it belongs to,
    // made once, and the buffers and scratch are the instance's.
    const now  = performance.now()
    const call = ++this._call
    for (const buf of this._kindBuffers.values()) buf.fill(0)
    for (const k in this._kindHas) this._kindHas[k] = false
    for (const k in this._active) delete this._active[k]
    this._playedNames.clear()

    // 1. The graph's Play Clip nodes, each on its own playhead.
    if (plays) {
      for (let i = 0; i < plays.length; i++) {
        const r = plays[i]
        if (!(r.weight > 0)) continue
        const clip = this._clips[r.clipName]
        if (!clip?.frames?.length) continue
        let st = r._face
        if (!st || st.name !== r.clipName) st = r._face = newPlayhead(r.clipName)
        this._playedNames.add(r.clipName)
        advance(st, now, call, r.loop !== false, r.speed ?? 1)
        this._accumulate(clip, st, r.weight)
      }
    }

    // 2. Clips the graph names with no play request behind them — and every
    //    clip, for a caller that passes no plays. One playhead per name.
    if (faceWeights) {
      for (const name in faceWeights) {
        if (name === '__livelink__' || this._playedNames.has(name)) continue
        const weight = faceWeights[name]
        if (!(weight > 0)) continue
        const clip = this._clips[name]
        if (!clip?.frames?.length) continue
        let st = this._byName[name]
        if (!st) st = this._byName[name] = newPlayhead(name)
        advance(st, now, call, true, 1)
        this._accumulate(clip, st, weight)
      }
    }

    // 3. Manual plays (PlayFaceClip, a gesture's face half, previews), each on
    //    the playhead it started with. Expired ones are pruned in the same pass.
    for (const name in this._overrides) {
      const o = this._overrides[name]
      if (o.expiresAt < now) {
        delete this._overrides[name]
        continue
      }
      const weight = overrideWeightAt(o, now)
      if (!(weight > 0)) continue
      const clip = this._clips[name]
      if (!clip?.frames?.length) continue
      if (!o.playhead) o.playhead = newPlayhead(name)
      advance(o.playhead, now, call, true, 1)
      this._accumulate(clip, o.playhead, weight)
    }

    return this._kindHas.arkit ? this._resultBuffer : null
  }

  /** Add `clip` at `st`'s time, times `weight`, into its kind's buffer. */
  _accumulate(clip, st, weight) {
    const frames = clip.frames
    const duration = frames[frames.length - 1].timeCode
    const t = clipTime(st, duration)

    // Walk forward from last frame; reset to 0 when time wraps past duration
    let frameIdx = st.lastFrameIdx
    if (frameIdx >= frames.length || frames[frameIdx].timeCode > t) frameIdx = 0
    while (frameIdx < frames.length - 1 && frames[frameIdx + 1].timeCode <= t) {
      frameIdx++
    }
    st.lastFrameIdx = frameIdx

    // Interpolate between current and next frame for smooth transitions
    // (especially across loop boundaries where values can jump).
    const weightsA = frames[frameIdx].blendshapeWeights
    if (!weightsA) return
    const kind = clip.kind ?? 'arkit'
    const result = this._bufferFor(kind, weightsA.length)
    const nextIdx = (frameIdx + 1) % frames.length
    const weightsB = frames[nextIdx].blendshapeWeights
    const tA = frames[frameIdx].timeCode
    const tB = nextIdx > frameIdx ? frames[nextIdx].timeCode : duration
    const frac = tB > tA ? (t - tA) / (tB - tA) : 0
    const limit = weightsA.length < result.length ? weightsA.length : result.length
    if (weightsB && frac > 0 && frac < 1) {
      for (let i = 0; i < limit; i++) {
        result[i] += (weightsA[i] + (weightsB[i] - weightsA[i]) * frac) * weight
      }
    } else {
      for (let i = 0; i < limit; i++) {
        result[i] += weightsA[i] * weight
      }
    }
    this._kindHas[kind] = true
    // Read back by the graph's anim-time rules (GraphEvaluator stateClipTiming),
    // and by the by-name view (writeTimingsInto, the anim-test panel), which
    // shows the heavier playhead of a clip.
    st.weight = weight
    st.duration = duration
    const shown = this._active[st.name]
    if (!shown || shown.weight < weight) this._active[st.name] = st
  }

  /** The output buffer for `kind` — ARKit is fixed at 52; another kind is
   *  sized by the first clip of it (allocated once per kind). */
  _bufferFor(kind, size) {
    let buf = this._kindBuffers.get(kind)
    if (!buf) {
      buf = new Float32Array(size)
      this._kindBuffers.set(kind, buf)
      this._kindHas[kind] = false
    }
    return buf
  }
}

/** A playhead: one clip's own clock. */
function newPlayhead(name) {
  return { name, local: 0, lastNow: 0, call: -1, loop: true, lastFrameIdx: 0, weight: 0, duration: 0 }
}

/**
 * Advance a playhead to `now`. Continuing = it played on the previous call;
 * anything else starts it at frame 0 (a new node, a re-entered state, a manual
 * play just started). Advanced by wall time × `speed`, so a speed that changes
 * mid-play changes the rate rather than jumping the playhead.
 */
function advance(st, now, call, loop, speed) {
  if (st.call === call) return                 // already advanced this call
  if (st.call === call - 1) st.local += ((now - st.lastNow) / 1000) * speed
  else { st.local = 0; st.lastFrameIdx = 0 }
  st.lastNow = now
  st.call = call
  st.loop = loop
}

/** The playhead's time within a clip of `duration`: wrapped when looping,
 *  held on the last frame when not. */
function clipTime(st, duration) {
  if (!(duration > 0)) return 0
  return st.loop ? st.local % duration : (st.local < duration ? st.local : duration)
}

export const faceClipPlayer = new FaceClipPlayer()

// Expose for e2e tests and debugging
if (typeof window !== 'undefined') {
  window.__FACE_CLIP_PLAYER__ = faceClipPlayer
}
